import React, { useLayoutEffect, useState, useEffect, useCallback } from "react";
import { Search, User, BookOpen, Loader2 } from 'lucide-react';
import { Stack } from "@mui/material";

/** icon **/
import { IoChevronBack } from "react-icons/io5";
import { GrSearch } from "react-icons/gr";

import { useNavbar } from "../../../context/NavBarContext";
import { useNavigate, useSearchParams } from "react-router-dom";

const AudioBookSearch = () => {

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Initialize searchTerm from URL parameter
    const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
    const [searchResults, setSearchResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

  // Debounced search function
  const debouncedSearch = useCallback(
    async (keyword) => {
      if (!keyword.trim()) {
        setSearchResults(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch('https://snsapiv2.baganit.com/api/app/audio/search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ keyword }),
        });

        if (!response.ok) {
          throw new Error('Search failed');
        }

        const data = await response.json();
        console.log(data);
        setSearchResults(data);
      } catch (err) {
        setError('Failed to search. Please try again.');
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Effect for debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim()) {
        debouncedSearch(searchTerm);
      } else {
        setSearchResults(null);
        setError(null);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);

  // Restore search on component mount if there's a query parameter
  useEffect(() => {
    const query = searchParams.get('q');
    if (query && query.trim()) {
      setSearchTerm(query);
      // Trigger search immediately for restored query
      debouncedSearch(query);
    }
  }, []); // Only run on mount

  const handleSearchChange = (e) => {
    const value = e.target.value;
    console.log('Input value:', value); // Debug log
    setSearchTerm(value);
    
    // Update URL parameter
    if (value.trim()) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
    
    // Clear previous results when input is empty
    if (!value.trim()) {
      setSearchResults(null);
      setError(null);
    }
  };

  const handleAuthorClick = (item) => {
      console.log("here");
      navigate(`/audio/books-by-author/${item.item_id}`);
  };

  const handleSeriesClick = (item) => {
      navigate(`/audio/book-series/${item.item_id}`);
  };

  const AuthorCard = ({ author }) => (
    <div className="flex gap-4 pb-4" onClick={() => handleAuthorClick(author)}>
        <div
            // onClick={() => handleClick(author)} // assuming handleClick is defined
            className="flex-shrink-0 w-20 py-2 h-auto flex flex-col items-center cursor-pointer"
            >
            <div className="relative w-16 h-16 mb-2">
                {author.item_image ? (
                <img
                    src={author.item_image}
                    alt={author.item_name}
                    className="w-full h-full object-cover rounded-full"
                />
                ) : (
                <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                </div>
                )}
            </div>
            {author.item_name && (
                <span className="text-center text-xs text-white truncate w-full">
                {author.item_name}
                </span>
            )}
            </div>
        </div>
  );

  const SeriesCard = ({ series }) => (
    
    <div>
        <div className="flex gap-2 " onClick={() => handleSeriesClick(series)}>
                {series.item_image && series.item_image !== '-' ? (
                    <img
                    src={series.item_image}
                    alt={series.item_name}
                    className="rounded-xl"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <BookOpen style={{ width: 32, height: 32, color: 'white' }} />
                )}
                </div>
                <div style={{ padding: '12px' }}>
                <div className="text-white-900 text-sm">
                    {series.item_name}
                </div>
        </div>
  </div>
  );

  const Navbar = useNavbar();

  useLayoutEffect(() => {
    Navbar.setLogoPlace("Search");
    Navbar.setTogglePlace(<IoChevronBack fontSize="20" />);
  }, []);



  return (
    <>
      <Stack spacing={1.5}>
        <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                <GrSearch className="text-amber-300" fontSize="20" />
            </span>
            <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                className={`pl-8 h-10 pr-2 bg-zinc-600 appearance-none rounded w-full leading-tight focus:outline-none focus:shadow-outline caret-yellow-500 rounded-full`}
            />
            {loading && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
                        </div>
                    )}
        </div>
      </Stack>

      <div>

      {/* Search Results */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {searchResults && (
        <div className="space-y-6">
          {/* Authors Section */}
          {searchResults.author && searchResults.author.length > 0 && (
            <div>
              <div className="flex items-center my-4">
                <h2 className="text-yellow-400 font-semibold">Authors</h2>
              </div>
              <div className="grid gap-3 grid-cols-4">
                {searchResults.author.map((author) => (
                  <AuthorCard key={author.item_id} author={author}   />
                ))}
              </div>
            </div>
          )}

          {/* Series Section */}
          {searchResults.series && searchResults.series.length > 0 && (
            <div>
              <div className="flex items-center mb-4">
                <div className="flex items-center my-4">
                    <h2 className="text-yellow-400 font-semibold">Series</h2>
                </div>
              </div>
              <div className="grid grid-cols-2 flex gap-4 pb-4">
                {searchResults.series.map((series) => (
                  <SeriesCard key={series.item_id} series={series} />
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {searchResults.author?.length === 0 && searchResults.series?.length === 0 && (
            <div className="text-center py-12">
              <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <Search className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
              <p className="text-gray-500">Try searching with different keywords</p>
            </div>
          )}
        </div>
      )}

    </div>

    </>
  );
};

export default AudioBookSearch;